import {
  Box,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Popper,
  Fade,
  Paper,
  MenuItem,
  Avatar,
  Typography,
} from "@mui/material";
import { Send, AttachFile, Cancel, EmojiEmotions } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { ChannelMemberDto } from "../../types/channelMember";
import EmojiPicker from 'emoji-picker-react';

interface Props {
  message: string;
  setMessage: (val: string) => void;
  handleSend: (files: File[]) => Promise<void>;
  isLoading: boolean;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  channelMembers?: ChannelMemberDto[];
  currentUserId?: number;
}

export const MessageInput = ({
  message,
  setMessage,
  handleSend,
  isLoading,
  files,
  setFiles,
  channelMembers = [],
  currentUserId = 0,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  // Nuevo estado para el modal de vista
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null
  );
  const [showMentionedUsers, setShowMentionedUsers] = useState(false);
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [filteredMembers, setFilteredMembers] = useState(channelMembers);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiAnchor = useRef(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleNewFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleNewFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList);
    const previews = newFiles.map((file) => URL.createObjectURL(file));
    setFiles((prev) => [...prev, ...newFiles]);
    setFilePreviews((prev) => [...prev, ...previews]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
    if (index === selectedFileIndex) {
      setPreviewOpen(false);
      setSelectedFileIndex(null);
    } else if (selectedFileIndex && index < selectedFileIndex) {
      setSelectedFileIndex((prev) => (prev !== null ? prev - 1 : null));
    }
  };

  const handleEmojiClick = (emojiObject: any) => {
    setMessage(message + emojiObject.emoji);
  };

  const onSend = async () => {
    if (!message.trim() && files.length === 0) return;
    await handleSend(files);
    setShowEmojiPicker(false);
    setMessage("");
    setFiles([]);
    setFilePreviews([]);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      await onSend();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => setIsDragActive(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files) {
      handleNewFiles(e.dataTransfer.files);
    }
  };

  const isVideo = (file: File) => file.type.startsWith("video/");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setMessage(value);

    const cursorPosition = e.target.selectionStart ?? 0;
    const textUntilCursor = value.substring(0, cursorPosition);
    const match = /@(\w*)$/.exec(textUntilCursor);

    if (match) {
      const searchTerm = match[1].toLowerCase();
      const filteredMembers = channelMembers.filter(
        (member) =>
          member.profile?.username
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) &&
          member.profileId !== currentUserId
      );
      setFilteredMembers(filteredMembers);
      setShowMentionedUsers(true);
    } else {
      setShowMentionedUsers(false);
    }
  };

  const handleMentionSelect = (member: any) => {
    const cursorPosition = message.length;
    const lastAtIndex = message.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const newMessage =
        message.substring(0, lastAtIndex) +
        `@${member.profile?.username} ` +
        message.substring(cursorPosition);

      setMessage(newMessage);
      setShowMentionedUsers(false);
    }
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        p: 1,
        borderTop: "1px solid #ddd",
        backgroundColor: isDragActive ? "#e8f5e9" : "#fafafa",
        transition: "background-color 0.3s",
      }}
    >
      {/* Previews */}
      {filePreviews.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {files.map((file, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                width: 90,
                height: 90,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedFileIndex(index);
                setPreviewOpen(true);
              }}
            >
              {isVideo(file) ? (
                <video
                  src={filePreviews[index]}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  muted
                />
              ) : (
                <img
                  src={filePreviews[index]}
                  alt={`preview-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                size="small"
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Input y botones */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Adjuntar archivos">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            sx={{fontSize: "1.5rem"}}
          >
            <AttachFile />
          </IconButton>
        </Tooltip>

        <Tooltip title="Seleccionar emoji">
          <IconButton
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            sx={{ fontSize: "1.5rem" }}
            ref={emojiAnchor}
          >
            <EmojiEmotions />
          </IconButton>
        </Tooltip>

        <input
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        <OutlinedInput
          placeholder="Escribe un mensaje..."
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setMentionAnchor(e.currentTarget);
            handleInputChange(e);
          }}
          size="small"
          sx={{ borderRadius: 8, backgroundColor: "#fff" }}
          endAdornment={
            <InputAdornment position="end" sx={{ mr: 0.5 }}>
              <IconButton
                color="success"
                onClick={onSend}
                disabled={isLoading || (!message.trim() && files.length === 0)}
                sx={{
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  color: "#fff",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0,
                  "&:hover": {
                    bgcolor: "success.dark",
                  },
                  "&:disabled": {
                    bgcolor: "grey.400",
                    color: "grey.600",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <Send fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          }
        />

        <Popper
          open={showEmojiPicker}
          anchorEl={emojiAnchor.current}
          placement="top-start"
          transition
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <Paper elevation={3}>
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </Paper>
            </Fade>
          )}
        </Popper>

        <Popper
          open={showMentionedUsers}
          anchorEl={mentionAnchor}
          placement="top-start"
          transition
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <Paper elevation={3} sx={{ maxHeight: 200, overflowY: "auto" }}>
                {filteredMembers.map((member) => (
                  <MenuItem
                    key={member.id}
                    onClick={() => handleMentionSelect(member)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Avatar
                      src={member.profile?.avatarUrl}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography variant="body2">
                      {member.profile?.username}
                    </Typography>
                  </MenuItem>
                ))}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>

      {/* Modal para ver archivos */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vista previa</DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          {selectedFileIndex !== null &&
            files[selectedFileIndex] &&
            (isVideo(files[selectedFileIndex]) ? (
              <video
                src={filePreviews[selectedFileIndex]}
                controls
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: 8,
                }}
              />
            ) : (
              <img
                src={filePreviews[selectedFileIndex]}
                alt="preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  borderRadius: 8,
                }}
              />
            ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
